/** Shared helpers for TV-1-00352 position template roles in role pickers. */

export const POSITION_TEMPLATE_CODE_PREFIX = 'POS_TEMPLATE_'

export const POSITION_TEMPLATE_SUBTITLE =
  'Position template — combined permissions for this job'

export function isPositionTemplateRole(role) {
  const code = role?.code || role?.value || ''
  return String(code).startsWith(POSITION_TEMPLATE_CODE_PREFIX)
}

/** Display label for v-select items (API roles use `name`, mapped items use `title`). */
export function rolePickerLabel(item) {
  return String(item?.title || item?.name || '')
}

/** Subtitle for list rows (Learning Center access panes, etc.). */
export function roleListSubtitle(role) {
  return isPositionTemplateRole(role) ? POSITION_TEMPLATE_SUBTITLE : undefined
}

/**
 * Sort role picker items so position templates appear first (alphabetical),
 * then all other roles (alphabetical). Works with API roles (`name`) and
 * mapped picker rows (`title`).
 */
export function sortRolesForPicker(items) {
  const templates = []
  const others = []
  for (const item of items || []) {
    if (isPositionTemplateRole(item)) {
      templates.push(item)
    } else {
      others.push(item)
    }
  }
  const byLabel = (a, b) => rolePickerLabel(a).localeCompare(rolePickerLabel(b))
  templates.sort(byLabel)
  others.sort(byLabel)
  return [...templates, ...others]
}

/**
 * Decorate API role rows for v-select (template subtitle + templates first).
 * Pass the result to :items; use item-props="props" when templates need subtitles.
 */
export function prepareRolesForPicker(roles) {
  return sortRolesForPicker(
    (roles || []).map((role) => ({
      ...role,
      props: isPositionTemplateRole(role)
        ? { subtitle: POSITION_TEMPLATE_SUBTITLE }
        : role.props,
    })),
  )
}

/** Prefer POS_TEMPLATE_TECHNICIAN when seeded; otherwise legacy TECHNICIAN. */
export function resolveTechnicianRoleCode(
  roles,
  preferredCode = 'POS_TEMPLATE_TECHNICIAN',
  legacyCode = 'TECHNICIAN',
) {
  const byLower = new Map()
  for (const role of roles || []) {
    const code = role?.code
    if (code) byLower.set(String(code).toLowerCase(), code)
  }
  const pick = (code) => byLower.get(String(code).toLowerCase())
  return pick(preferredCode) || pick(legacyCode) || ''
}

/** Map onboarding defaults roles to v-select rows (templates first). */
export function onboardingRoleItems(roles) {
  return sortRolesForPicker(
    (roles || []).map((r) => ({
      title: r.name,
      value: r.code,
      code: r.code,
      props: isPositionTemplateRole(r)
        ? { subtitle: POSITION_TEMPLATE_SUBTITLE }
        : undefined,
    })),
  )
}
